import { Icon } from '@/components/ui';
import { cn } from '@/utils/cn';

import { PIPELINE_STEPS, furthestStep, stepIndex } from './status';

const CLOSED_LABELS = {
  REJECTED: 'Not taken forward',
  WITHDRAWN: 'Withdrawn by the candidate',
};

/**
 * The recruitment process as one row of steps: done, current, still ahead.
 *
 * Shared by both sides. A closed application keeps the steps it reached and
 * says where it stopped, so "rejected after two rounds" reads differently
 * from "rejected on sight". `rounds` adds the round count under Interview.
 */
export function RecruitmentProgress({ status, history = [], rounds = 0, labels = {} }) {
  const closed = status === 'REJECTED' || status === 'WITHDRAWN';
  const current = closed ? furthestStep(history) : stepIndex(status);
  // HIRED is the last step and is done, not "in progress".
  const finished = status === 'HIRED';

  return (
    <div>
      <div className="overflow-x-auto pb-1">
        <ol className="grid min-w-[36rem] grid-cols-8">
          {PIPELINE_STEPS.map((step, index) => {
            const done = index < current || (finished && index === current);
            const isCurrent = index === current && !finished && !closed;
            const stoppedHere = closed && index === current;
            const note =
              step.key === 'interview' && rounds > 0
                ? `${rounds} ${rounds === 1 ? 'round' : 'rounds'}`
                : null;
            return (
              <li key={step.key} className="relative flex flex-col items-center text-center">
                {index > 0 && (
                  <span
                    aria-hidden="true"
                    className={cn(
                      'absolute top-3.5 right-1/2 h-0.5 w-full -translate-y-1/2',
                      index <= current ? 'bg-brand-500' : 'bg-line',
                    )}
                  />
                )}
                <span
                  className={cn(
                    'relative z-10 grid size-7 place-items-center rounded-full text-xs font-semibold transition',
                    done && 'bg-brand-600 text-white shadow-brand-600/30 shadow-sm',
                    isCurrent && 'bg-surface text-brand-700 ring-brand-500 ring-2 ring-offset-2',
                    stoppedHere && 'bg-danger-50 text-danger-600 ring-danger-200 ring-2',
                    !done && !isCurrent && !stoppedHere && 'bg-surface-muted text-ink-muted',
                  )}
                >
                  {done ? (
                    <Icon name="check" size={14} />
                  ) : stoppedHere ? (
                    <Icon name="close" size={13} />
                  ) : (
                    index + 1
                  )}
                </span>
                <span
                  className={cn(
                    'mt-2 text-xs leading-tight',
                    isCurrent ? 'text-brand-700 font-semibold' : 'text-ink-muted',
                    done && 'text-ink',
                  )}
                >
                  {labels[step.key] ?? step.label}
                </span>
                {note && <span className="text-ink-muted mt-0.5 text-[11px]">{note}</span>}
              </li>
            );
          })}
        </ol>
      </div>
      {closed && (
        <p className="text-danger-700 bg-danger-50 rounded-input mt-4 px-3 py-2 text-xs font-medium">
          {CLOSED_LABELS[status]} at the “{PIPELINE_STEPS[current]?.label}” step.
        </p>
      )}
    </div>
  );
}
